import { Link } from "react-router-dom";
import { ArrowRight, RefreshCw, Check } from "lucide-react";
import ComplianceStrip from "../components/ComplianceStrip";
import GuideMeta from "../components/GuideMeta";
import ConveyancingCTA from "../components/ConveyancingCTA";
import BrokerCTA from "../components/BrokerCTA";
import Seo from "../components/Seo";
import { REMO_SECTIONS as sections } from "../content/site";

const Bullet = ({ children }) => (
  <li className="flex gap-3 text-[15px] text-[#475569] leading-relaxed">
    <Check className="w-4 h-4 mt-1 shrink-0 text-[#10B981]" />
    <span>{children}</span>
  </li>
);

const SubItem = ({ h, b }) => (
  <div className="rounded-xl bg-[#F7FAF9] border border-[#DCE7E3] p-5">
    <div className="font-sora text-base font-semibold text-[#0B1220]">{h}</div>
    <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{b}</p>
  </div>
);

const Section = ({ s }) => (
  <article id={s.id} className="mp-card p-6 sm:p-8 scroll-mt-24">
    <h2 className="font-sora text-2xl sm:text-[1.65rem] font-semibold text-[#0B1220] tracking-tight">{s.h}</h2>
    {s.intro && <p className="mt-3 text-[#475569] leading-relaxed text-[15px]">{s.intro}</p>}
    {s.body && <p className="mt-3 text-[#475569] leading-relaxed text-[15px]">{s.body}</p>}
    {s.paragraphs?.map((p) => (
      <p key={p.slice(0, 40)} className="mt-3 text-[#475569] leading-relaxed text-[15px]">{p}</p>
    ))}
    {s.bullets && (
      <ul className="mt-5 space-y-2.5">
        {s.bullets.map((b) => <Bullet key={b.slice(0, 40)}>{b}</Bullet>)}
      </ul>
    )}
    {s.items && (
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {s.items.map((it) => <SubItem key={it.h} {...it} />)}
      </div>
    )}
  </article>
);

const Remortgage = () => (
  <div className="mp-container py-14">
    <Seo
      title="UK Remortgage Guide — when to start & what to expect"
      description="Plain-English UK remortgage guide. Product transfer vs full remortgage, LTV and equity, affordability checks, early repayment charges, valuation and legal work. Educational only."
      path="/remortgage-guide"
      type="article"
    />
    <div className="max-w-3xl">
      <div className="text-xs uppercase tracking-widest text-[#0F766E] flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5" /> Educational guide</div>
      <h1 className="font-sora mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-[#0B1220]">UK Remortgage Guide</h1>
      <p className="mt-4 text-[#475569] leading-relaxed text-base sm:text-lg max-w-2xl">When to start, how product transfers compare to full remortgages, loan-to-value and equity, early repayment charges, costs, and how to time things around your current deal.</p>
      <GuideMeta lastReviewed="July 2026" readTime="10 min read" />
    </div>

    <div className="grid lg:grid-cols-4 gap-8 mt-10">
      <aside className="hidden lg:block lg:col-span-1">
        <div className="sticky top-24 mp-card p-5">
          <div className="text-xs uppercase tracking-widest text-[#0F766E] mb-3">On this page</div>
          <ul className="space-y-2 text-sm">
            {sections.map((s) => (
              <li key={s.id}><a href={`#${s.id}`} className="text-[#475569] hover:text-[#0F766E] block leading-snug">{s.h}</a></li>
            ))}
          </ul>
        </div>
      </aside>
      <div className="lg:col-span-3 space-y-6">
        {sections.map((s) => <Section key={s.id} s={s} />)}
        <BrokerCTA source="guide_remortgage_broker" variant="clickable-card" className="mt-0" />
        <ConveyancingCTA source="guide_remortgage_conveyancing" variant="clickable-card" className="mt-0" />
        <div className="grid sm:grid-cols-2 gap-4 pt-2">
          <Link to="/snapshot" className="mp-card p-6 mp-card-hover" data-testid="remo-cta-snapshot">
            <div className="text-xs uppercase tracking-widest text-[#0F766E]">Next step</div>
            <div className="font-sora text-xl font-semibold mt-1">Start your remortgage snapshot</div>
            <div className="text-sm text-[#475569] mt-1 inline-flex items-center gap-1">See your equity and LTV <ArrowRight className="w-4 h-4" /></div>
          </Link>
          <Link to="/calculator" className="mp-card p-6 mp-card-hover" data-testid="remo-cta-calc">
            <div className="text-xs uppercase tracking-widest text-[#0F766E]">Or estimate</div>
            <div className="font-sora text-xl font-semibold mt-1">Estimate your repayments</div>
            <div className="text-sm text-[#475569] mt-1 inline-flex items-center gap-1">Try overpayments and term changes <ArrowRight className="w-4 h-4" /></div>
          </Link>
        </div>
        <ComplianceStrip />
      </div>
    </div>
  </div>
);

export default Remortgage;
